import React, { useEffect, useRef, useState } from 'react';
import { Building2, Globe2, House, LayoutGrid, Loader2, Settings2, X } from 'lucide-react';

import { createParentProject, loadProjectConfiguration, saveProjectConfiguration } from './api';
import { XomicsSelect } from './parts';
import { tx } from './i18n';
import { errorMessage } from './ui';

function Dialog({ title, subtitle, busy, unavailable = false, error, onClose, onSubmit, submitLabel, children }) {
  const dialog = useRef(null);
  const close = useRef(onClose);
  const saving = useRef(busy);
  close.current = onClose;
  saving.current = busy;
  useEffect(() => {
    const previous = document.activeElement;
    dialog.current?.querySelector('input, button')?.focus();
    const keyboard = event => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape' && !saving.current) close.current();
      if (event.key !== 'Tab') return;
      const controls = [...dialog.current.querySelectorAll('button:not(:disabled), input:not(:disabled), [tabindex="0"]')];
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    window.addEventListener('keydown', keyboard);
    return () => { window.removeEventListener('keydown', keyboard); previous?.focus(); };
  }, []);
  return (
    <div className="xo-dialog-layer" onMouseDown={event => {
      if (event.target === event.currentTarget && !busy) onClose();
    }}>
      <form ref={dialog} className="xo-dialog xo-project-settings-dialog" role="dialog"
        aria-modal="true" aria-labelledby="xo-project-settings-title" onSubmit={onSubmit}>
        <header className="xo-dialog-head">
          <span className="xo-project-settings-emblem"><Settings2 aria-hidden="true" /></span>
          <div><h2 id="xo-project-settings-title">{title}</h2><p>{subtitle}</p></div>
          <button type="button" className="xo-dialog-close" disabled={busy} onClick={onClose}
            aria-label={tx('ui.parts.close')}><X aria-hidden="true" /></button>
        </header>
        <div className="xo-dialog-body">{children}</div>
        {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
        <footer className="xo-dialog-foot">
          <button type="button" className="xo-btn-2" disabled={busy} onClick={onClose}>{tx('ui.parts.cancel')}</button>
          <button type="submit" className="xo-btn" disabled={busy || unavailable}>
            {busy && <Loader2 className="xo-spin" aria-hidden="true" />}{submitLabel}
          </button>
        </footer>
      </form>
    </div>
  );
}

export function NewProjectDialog({ onCreated, onClose }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(event) {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try { onCreated(await createParentProject(name)); }
    catch (caught) { setError(errorMessage(caught)); setBusy(false); }
  }
  return (
    <Dialog title={tx('ui.projects.settings.new_project')} subtitle={tx('ui.projects.settings.new_defaults')}
      busy={busy} unavailable={name.trim() === ''} error={error} onClose={onClose} onSubmit={submit}
      submitLabel={tx('ui.projects.settings.create')}>
      <label className="xo-project-setting-field">
        <span>{tx('ui.projects.settings.project_name')}</span>
        <input value={name} onChange={event => setName(event.target.value)} maxLength={255} required disabled={busy} />
      </label>
    </Dialog>
  );
}

export function ProjectSettingsDialog({ project, onClose }) {
  const [model, setModel] = useState(null);
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setError('');
    loadProjectConfiguration(project.id).then(data => {
      if (!active) return;
      setModel(data); setValues(Object.fromEntries(data.settings.map(row => [row.key, row.value])));
    }).catch(caught => { if (active) setError(errorMessage(caught)); });
    return () => { active = false; };
  }, [project.id, attempt]);
  const change = (key, value) => setValues(current => ({ ...current, [key]: value }));
  async function submit(event) {
    event.preventDefault();
    if (busy || model === null) return;
    setBusy(true); setError('');
    try {
      await saveProjectConfiguration(project.id, model.options.map(option => ({ key: option.key, value: values[option.key] })));
      onClose();
    } catch (caught) { setError(errorMessage(caught)); setBusy(false); }
  }
  return (
    <Dialog title={tx('ui.projects.settings.title')} subtitle={project.name} busy={busy} unavailable={model === null}
      error={error} onClose={onClose} onSubmit={submit} submitLabel={tx('ui.projects.settings.save')}>
      {model === null && !error && <div className="xo-project-settings-loading"><Loader2 className="xo-spin" aria-hidden="true" />{tx('ui.projects.settings.loading')}</div>}
      {model === null && error && <button type="button" className="xo-btn-2" onClick={() => setAttempt(value => value + 1)}>{tx('ui.projects.settings.retry')}</button>}
      {model !== null && <>
        <div className="xo-project-settings-identities">
          <label className="xo-project-setting-field">
            <span><Globe2 aria-hidden="true" />{tx('ui.projects.settings.language')}</span>
            <XomicsSelect value={values.language} disabled={busy} onChange={event => change('language', event.target.value)} aria-label={tx('ui.projects.settings.language')}>
              {model.languages.map(language => <option key={language.code} value={language.code}>{language.name}</option>)}
            </XomicsSelect>
          </label>
          <label className="xo-project-setting-field">
            <span><Building2 aria-hidden="true" />{tx('ui.projects.settings.company')}</span>
            <XomicsSelect value={values.company === null ? '' : String(values.company)} disabled={busy}
              onChange={event => change('company', event.target.value === '' ? null : Number(event.target.value))} aria-label={tx('ui.projects.settings.company')}>
              <option value="">{tx('ui.projects.settings.no_company')}</option>
              {model.companies.map(company => <option key={company.id} value={String(company.id)}>{company.name}</option>)}
            </XomicsSelect>
          </label>
        </div>
        <div className="xo-project-settings-groups">
          {[{ kind: 'page', title: 'pages', Icon: LayoutGrid }, { kind: 'home_section', title: 'home_sections', Icon: House }].map(({ kind, title, Icon }) => {
            const options = model.options.filter(option => option.kind === kind);
            return <section key={kind} className="xo-project-settings-group">
              <header><h3><Icon aria-hidden="true" />{tx(`ui.projects.settings.${title}`)}</h3><span>{options.filter(option => values[option.key]).length}/{options.length}</span></header>
              {options.map(option => <label key={option.key} className="xo-project-setting-toggle">
                <span>{tx(option.labelKey)}</span>
                <input type="checkbox" role="switch" checked={values[option.key]} disabled={busy}
                  onChange={event => change(option.key, event.target.checked)} />
                <i aria-hidden="true" />
              </label>)}
            </section>;
          })}
        </div>
      </>}
    </Dialog>
  );
}
